// Small shared helpers.

export const uid = (p = "id"): string =>
  `${p}_${Math.random().toString(36).slice(2, 9)}${Date.now().toString(36).slice(-4)}`;

export const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

/** decibel <-> linear gain */
export const dbToGain = (db: number): number => (db <= -60 ? 0 : Math.pow(10, db / 20));
export const gainToDb = (g: number): number => (g <= 0.0001 ? -Infinity : 20 * Math.log10(g));

export const fmtTime = (sec: number): string => {
  if (!isFinite(sec)) sec = 0;
  const sign = sec < 0 ? "-" : "";
  sec = Math.abs(sec);
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec - Math.floor(sec)) * 1000);
  return `${sign}${m}:${s.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
};

/** Bars:beats from seconds given tempo + beats-per-bar. */
export const fmtBars = (sec: number, bpm: number, beatsPerBar = 4): string => {
  const beats = (sec * bpm) / 60;
  const bar = Math.floor(beats / beatsPerBar) + 1;
  const beat = Math.floor(beats % beatsPerBar) + 1;
  const tick = Math.floor(((beats % 1) * 480));
  return `${bar}.${beat}.${tick.toString().padStart(3, "0")}`;
};

export const secPerBeat = (bpm: number): number => 60 / bpm;

/** Create an element with optional class/attrs/children. Tiny DOM helper. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: { class?: string; text?: string; html?: string; attrs?: Record<string, string>; on?: Partial<Record<keyof HTMLElementEventMap, (e: any) => void>> } = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text != null) node.textContent = opts.text;
  if (opts.html != null) node.innerHTML = opts.html;
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
  if (opts.on) for (const [k, fn] of Object.entries(opts.on)) node.addEventListener(k, fn as any);
  for (const c of children) node.append(c as any);
  return node;
}

const PALETTE = ["#5b8cff", "#ff6b6b", "#22c55e", "#eab308", "#a855f7", "#06b6d4", "#f97316", "#ec4899"];
export const pickColor = (i: number): string => PALETTE[i % PALETTE.length];

export type Listener<T> = (payload: T) => void;
/** Minimal typed event emitter. */
export class Emitter<Events extends Record<string, any>> {
  private map = new Map<keyof Events, Set<Listener<any>>>();
  on<K extends keyof Events>(ev: K, fn: Listener<Events[K]>): () => void {
    let s = this.map.get(ev);
    if (!s) this.map.set(ev, (s = new Set()));
    s.add(fn);
    return () => s!.delete(fn);
  }
  emit<K extends keyof Events>(ev: K, payload: Events[K]): void {
    this.map.get(ev)?.forEach((fn) => fn(payload));
  }
}

/** Write an inline style only when it changes (meters run every animation frame;
 *  redundant writes force style recalcs even when nothing is playing). */
export function setStyle(node: HTMLElement, prop: "width" | "height" | "left" | "bottom", pct: number) {
  const v = Math.min(100, Math.max(0, pct)).toFixed(1) + "%";
  if ((node as any)["_s_" + prop] !== v) { (node as any)["_s_" + prop] = v; node.style[prop] = v; }
}
